/* eslint-disable lingui/no-unlocalized-strings -- U02: legacy wire values and synthetic backend data; UI uses Lingui. */
import { useLingui } from '@lingui/react/macro';
import { useEffect, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import {
  CheckboxFormField,
  DateFormField,
  MoneyFormField,
  PercentFormField,
  SelectFormField,
  TextFormField,
} from '@/shared/form';
import { formatCivilDate } from '@/shared/format/date';
import { formatCents } from '@/shared/format/money';
import { FormField } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';

import { lookupBankSlipZipCode } from './bank-slip.api';
import type { BankSlipAction } from './bank-slip.status-actions';
import type { BankSlip } from './bank-slip.wire';

export function BankSlipFields({
  ctx,
}: {
  readonly ctx: { readonly action: BankSlipAction; readonly bankSlip: BankSlip };
}) {
  const { t, i18n } = useLingui();
  const { setValue, control } = useFormContext<{
    [key: string]: unknown;
    payer: { zipCode?: string; addressName?: string } | null;
    interestAmount: number | null;
    interestCodeValue: string | null;
    fineAmount: number | null;
  }>();
  const zipField = useController({ control, name: 'payer.zipCode' });
  const [interest, fine, instruction] = useWatch<Record<string, unknown>>({
    name: ['interestTypeValue', 'fineTypeValue', 'firstInstructionDebtCollection'],
  });
  const [zipError, setZipError] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  useEffect(() => {
    if (ctx.action !== 'updateOtherInformation') return;
    if (interest === 'Free') {
      setValue('interestAmount', 0);
      setValue('interestCodeValue', null);
    }
    if (fine === 'Free') setValue('fineAmount', 0);
  }, [ctx.action, interest, fine, setValue]);
  const currentAmount = (
    <p>
      {t`Valor atual da cobrança`}:{' '}
      <strong>{formatCents(ctx.bankSlip.amount, { locale: i18n.locale })}</strong>
    </p>
  );
  if (ctx.action === 'updateDueDate')
    return (
      <>
        <h3 className="font-semibold">{t`Dados da cobrança`}</h3>
        <p>
          {t`Data limite de pagamento do boleto`}:{' '}
          <strong>
            {ctx.bankSlip.overdueLimitDate
              ? formatCivilDate(ctx.bankSlip.overdueLimitDate, { locale: i18n.locale })
              : t`N/D`}
          </strong>
        </p>
        <h3 className="font-semibold">{t`Novo vencimento da cobrança`}</h3>
        <DateFormField name="dueDate" label={t`Data de vencimento da cobrança`} required />
        {currentAmount}
      </>
    );
  if (ctx.action === 'updateAmount')
    return (
      <>
        {currentAmount}
        <MoneyFormField name="amount" label={t`Novo valor do boleto`} required />
      </>
    );
  if (ctx.action === 'insertDeduction')
    return (
      <>
        <h3 className="font-semibold">{t`Dados da cobrança`}</h3>
        {currentAmount}
        <h3 className="font-semibold">{t`Valor do abatimento`}</h3>
        <MoneyFormField name="deduction" label={t`Valor do abatimento`} required />
      </>
    );
  if (ctx.action === 'cancel' || ctx.action === 'cancelProtest')
    return <CheckboxFormField name="keepBillingOpen" label={t`Manter carteira aberta`} />;
  if (ctx.action === 'transfer')
    return <TextFormField name="toWalletCode" label={t`Selecione a carteira`} required />;
  if (ctx.action === 'download')
    return (
      <>
        <SelectFormField
          name="walletCodeType"
          label={t`Carteira`}
          options={[
            { value: 'originWalletCode', label: t`Carteira de origem` },
            { value: 'currentWalletCode', label: t`Carteira atual` },
          ]}
        />
        <CheckboxFormField name="useUpdated" label={t`Boleto atualizado`} />
      </>
    );
  if (ctx.action === 'settlement')
    return (
      <>
        <h3 className="font-semibold">{t`Dados da cobrança`}</h3>
        {currentAmount}
        <h3 className="font-semibold">{t`Novo valor da cobrança`}</h3>
        <SelectFormField
          name="confirmationTransactionTypeValue"
          label={t`Tipo de confirmação`}
          required
          options={[
            { value: 'LiquidacaoNormal', label: t`Liquidação Normal` },
            { value: 'LiquidacaoCartorio', label: t`Liquidação Cartório` },
          ]}
        />
        <MoneyFormField name="paidAmount" label={t`Valor líquido`} required />
        <TextFormField name="paymentISPB" label={t`ISPB`} inputMode="numeric" />
        <TextFormField name="paymentAgency" label={t`Agência`} inputMode="numeric" />
        <DateFormField name="paymentDate" label={t`Data de liquidação`} />
      </>
    );
  if (ctx.action !== 'updateOtherInformation') return null;
  const fees = [
    { value: 'Free', label: t`Isento` },
    { value: 'Money', label: t`Valor fixo` },
    { value: 'Percent', label: t`Percentual` },
  ];
  return (
    <>
      <h3 className="font-semibold">{t`Informações básicas`}</h3>
      <TextFormField name="documentNumber" label={t`Número do documento`} />
      <TextFormField name="numberPayments" label={t`Número de pagamentos`} />
      <SelectFormField name="interestTypeValue" label={t`Tipo de juros`} options={fees} />
      {interest === 'Percent' ? (
        <PercentFormField name="interestAmount" label={t`Taxa de juros mora ao mês %`} />
      ) : (
        <MoneyFormField
          name="interestAmount"
          label={t`Taxa de juros mora ao dia R$`}
          disabled={interest === 'Free'}
        />
      )}
      {(interest === 'Money' || interest === 'Percent') && (
        <SelectFormField
          name="interestCodeValue"
          label={t`Código de juros`}
          options={[
            { value: 'ValorDiasCorridos', label: t`Dias corridos` },
            { value: 'ValorDiasUteis', label: t`Dias úteis` },
          ]}
        />
      )}
      <SelectFormField name="fineTypeValue" label={t`Tipo de multa`} options={fees} />
      {fine === 'Percent' ? (
        <PercentFormField name="fineAmount" label={t`Multa por atraso`} />
      ) : (
        <MoneyFormField name="fineAmount" label={t`Multa por atraso`} disabled={fine === 'Free'} />
      )}
      <h3 className="font-semibold">{t`Desconto`}</h3>
      <DateFormField name="discountLimitDate" label={t`Data limite de desconto`} />
      <MoneyFormField name="discount" label={t`Desconto`} />
      <h3 className="font-semibold">{t`Pagador`}</h3>
      <TextFormField name="payer.name" label={t`Nome do pagador`} />
      <TextFormField name="payer.email" label={t`E-mail do pagador`} />
      <FormField
        name="payer.zipCode"
        label={t`CEP`}
        disabled={zipBusy}
        error={zipField.fieldState.error?.message}
      >
        {(field) => (
          <Input
            {...field}
            ref={zipField.field.ref}
            inputMode="numeric"
            value={zipField.field.value ?? ''}
            onBlur={zipField.field.onBlur}
            onChange={(event) => {
              const zip = event.target.value.replace(/\D/g, '');
              zipField.field.onChange(zip);
              if (zip.length !== 8) return;
              setZipBusy(true);
              setZipError(false);
              void lookupBankSlipZipCode(zip)
                .then((result) => setValue('payer.addressName', result.address))
                .catch(() => setZipError(true))
                .finally(() => setZipBusy(false));
            }}
          />
        )}
      </FormField>
      {zipError && (
        <p role="alert" className="text-destructive">{t`Não foi possível consultar o CEP`}</p>
      )}
      <TextFormField name="payer.addressName" label={t`Endereço`} />
      <TextFormField name="payer.addressNumber" label={t`Número`} />
      <TextFormField name="payer.addressComplement" label={t`Complemento`} />
      <h3 className="font-semibold">{t`Sacador avalista`}</h3>
      <TextFormField name="guarantor.name" label={t`Nome`} />
      <TextFormField name="guarantor.registrationNumber" label={t`CPF/CNPJ`} />
      <h3 className="font-semibold">{t`Instruções`}</h3>
      <SelectFormField
        name="firstInstructionDebtCollection"
        label={t`Instrução de protesto`}
        options={[
          { value: '00', label: t`Não protestar` },
          { value: '06', label: t`Protestar automaticamente` },
        ]}
      />
      {instruction === '06' && (
        <TextFormField
          name="secondInstructionDebtCollection"
          label={t`Nº de dias após vencimento para envio automático`}
          inputMode="numeric"
          required
        />
      )}
      <h3 className="font-semibold">{t`Mensagem do boleto`}</h3>
      <TextFormField name="description" label={t`Mensagem`} />
    </>
  );
}
